/**
 * EdgeMail Management Page
 * Main entry point for the WeBeMail Admin Console
 * Mirrors Document Management dashboard styling
 */
import React, { useState } from 'react';
import {
  ChartBarIcon,
  EnvelopeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ArrowsRightLeftIcon,
  ArrowUpTrayIcon,
  CogIcon,
  Squares2X2Icon,
  IdentificationIcon,
  NoSymbolIcon,
  CircleStackIcon,
} from '@heroicons/react/24/outline';
import { AdminDashboardTab } from '../components/tabs/AdminDashboardTab';
import { MailboxListView } from '../components/tabs/MailboxListView';
import { MigrationView } from '../components/tabs/MigrationView';
import { GroupListView } from '../components/tabs/GroupListView';
import { DomainListView } from '../components/tabs/DomainListView';
import BlockedSendersView from '../components/tabs/BlockedSendersView';
import RejectedMailView from '../components/tabs/RejectedMailView';
import EvidenceIndexingView from '../components/tabs/EvidenceIndexingView';
import { CreateMailboxWizard } from '../components/wizards/CreateMailboxWizard';
import { EditMailboxPanel } from '../components/panels/EditMailboxPanel';
import { CreateGroupModal } from '../components/modals/CreateGroupModal';
import { GroupMemberManager } from '../components/panels/GroupMemberManager';
import { TenantProvider } from '../context/TenantContext';
import type { Mailbox, DistributionGroup } from '../types/admin.types';

// Types for navigation
type AdminSection =
  | 'dashboard'
  | 'mailboxes'
  | 'groups'
  | 'resources'
  | 'contacts'
  | 'domains'
  | 'sharing'
  | 'compliance'
  | 'mailflow'
  | 'migration'
  | 'blocked-senders'
  | 'rejected-mail'
  | 'evidence-indexing';

interface NavItem {
  id: AdminSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  implemented: boolean;
  comingSoon?: string;
}

interface NavSection {
  id: string;
  label: string;
  items: NavItem[];
}

// Navigation structure matching Exchange Admin Center
const ADMIN_NAVIGATION: NavSection[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      {
        id: 'dashboard',
        label: 'Dashboard',
        icon: ChartBarIcon,
        description: 'Health metrics, storage, queue status, and alerts',
        implemented: true,
      },
    ],
  },
  {
    id: 'recipients',
    label: 'Recipients',
    items: [
      {
        id: 'mailboxes',
        label: 'Mailboxes',
        icon: EnvelopeIcon,
        description: 'User mailboxes, quotas, and settings',
        implemented: true,
      },
      {
        id: 'groups',
        label: 'Distribution Groups',
        icon: UserGroupIcon,
        description: 'Email distribution lists and membership',
        implemented: true,
      },
      {
        id: 'resources',
        label: 'Resources',
        icon: BuildingOfficeIcon,
        description: 'Conference rooms and equipment',
        implemented: false,
        comingSoon: 'Phase 2',
      },
      {
        id: 'contacts',
        label: 'Contacts',
        icon: IdentificationIcon,
        description: 'External contacts and GAL',
        implemented: false,
        comingSoon: 'Phase 2',
      },
    ],
  },
  {
    id: 'organization',
    label: 'Organization',
    items: [
      {
        id: 'domains',
        label: 'Domains',
        icon: GlobeAltIcon,
        description: 'Custom domains and DNS verification',
        implemented: true,
      },
      {
        id: 'sharing',
        label: 'Sharing Policies',
        icon: Squares2X2Icon,
        description: 'Calendar and contact sharing rules',
        implemented: false,
        comingSoon: 'Phase 3',
      },
    ],
  },
  {
    id: 'security',
    label: 'Security',
    items: [
      {
        id: 'blocked-senders',
        label: 'Blocked Senders',
        icon: NoSymbolIcon,
        description: 'Addresses and domains auto-filed to Junk',
        implemented: false,
        comingSoon: 'WeBeMail fork',
      },
      {
        id: 'rejected-mail',
        label: 'Rejected Mail',
        icon: ShieldExclamationIcon,
        description: 'Mail flagged or rejected by spam scoring',
        implemented: false,
        comingSoon: 'WeBeMail fork',
      },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance',
    items: [
      {
        id: 'compliance',
        label: 'Compliance Center',
        icon: ShieldCheckIcon,
        description: 'eDiscovery, litigation hold, and audit',
        implemented: false,
        comingSoon: 'Phase 3',
      },
      {
        id: 'evidence-indexing',
        label: 'Evidence Indexing',
        icon: CircleStackIcon,
        description: 'Email embeddings, schedules, previews, and run history',
        implemented: false,
        comingSoon: 'WeBeMail fork',
      },
    ],
  },
  {
    id: 'mailflow',
    label: 'Mail Flow',
    items: [
      {
        id: 'mailflow',
        label: 'Mail Flow',
        icon: ArrowsRightLeftIcon,
        description: 'Message trace, queues, and transport rules',
        implemented: false,
        comingSoon: 'Phase 4',
      },
      {
        id: 'migration',
        label: 'Migration',
        icon: ArrowUpTrayIcon,
        description: 'Import from IMAP/PST sources',
        implemented: false,
        comingSoon: 'WeBeMail fork',
      },
    ],
  },
];

/**
 * Admin Sidebar Navigation Component
 */
interface AdminSidebarProps {
  activeSection: AdminSection;
  onSectionChange: (sectionId: AdminSection) => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ activeSection, onSectionChange }) => {
  return (
    <div className="w-full lg:w-72 bg-gray-50 dark:bg-gray-800 lg:border-r border-gray-200 dark:border-gray-700 lg:min-h-full">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center space-x-2 mb-6">
          <EnvelopeIcon className="h-6 w-6 text-lex-accent" />
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            WeBeMail Admin
          </h1>
        </div>

        {/* Navigation Sections */}
        <nav className="space-y-6">
          {ADMIN_NAVIGATION.map((section) => (
            <div key={section.id}>
              <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 px-3">
                {section.label}
              </h2>
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = activeSection === item.id;
                  const Icon = item.icon;

                  return (
                    <button
                      key={item.id}
                      onClick={() => item.implemented && onSectionChange(item.id)}
                      disabled={!item.implemented}
                      className={`w-full flex items-start space-x-3 px-3 py-2 rounded-lg text-left transition-all duration-200 group ${
                        isActive
                          ? 'bg-lex-accent-soft text-lex-accent border border-lex-accent'
                          : item.implemented
                          ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                          : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                          isActive
                            ? 'text-lex-accent'
                            : item.implemented
                            ? 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                            : 'text-gray-300 dark:text-gray-600'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm truncate">{item.label}</span>
                          {!item.implemented && item.comingSoon && (
                            <span className="text-xs bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded font-medium">
                              {item.comingSoon}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">
                          {item.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </div>
    </div>
  );
};

/**
 * Placeholder for unimplemented sections
 */
interface PlaceholderContentProps {
  item: NavItem;
}

const PlaceholderContent: React.FC<PlaceholderContentProps> = ({ item }) => {
  const Icon = item.icon;

  return (
    <div className="flex items-center justify-center min-h-[400px] px-4">
      <div className="text-center max-w-md w-full">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/20 mb-4">
          <Icon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.label}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{item.description}</p>
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 text-amber-700 dark:text-amber-300">
            <CogIcon className="h-4 w-4" />
            <span className="text-sm font-medium">Coming in {item.comingSoon}</span>
          </div>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
            This feature is currently under development.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Find nav item by section ID
 */
function findNavItem(sectionId: AdminSection): NavItem | undefined {
  for (const section of ADMIN_NAVIGATION) {
    const item = section.items.find((i) => i.id === sectionId);
    if (item) return item;
  }
  return undefined;
}

/**
 * EdgeMail Management Page Component
 */
const EdgeMailManagementPage: React.FC = () => {
  return (
    <TenantProvider>
      <EdgeMailManagementContent />
    </TenantProvider>
  );
};

const EdgeMailManagementContent: React.FC = () => {
  const [activeSection, setActiveSection] = useState<AdminSection>('dashboard');

  // Mailbox wizard and panel state
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [selectedMailbox, setSelectedMailbox] = useState<Mailbox | null>(null);

  // Distribution Group state
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showGroupMemberManager, setShowGroupMemberManager] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<DistributionGroup | null>(null);

  // Mailbox Handlers
  const handleCreateMailbox = () => {
    setShowCreateWizard(true);
  };

  const handleEditMailbox = (mailbox: Mailbox) => {
    setSelectedMailbox(mailbox);
    setShowEditPanel(true);
  };

  const handleViewMailbox = (mailbox: Mailbox) => {
    // For now, open edit panel in view mode
    setSelectedMailbox(mailbox);
    setShowEditPanel(true);
  };

  const handleCloseEditPanel = () => {
    setShowEditPanel(false);
    setSelectedMailbox(null);
  };

  // Distribution Group Handlers
  const handleCreateGroup = () => {
    setShowCreateGroupModal(true);
  };

  const handleEditGroup = (group: DistributionGroup) => {
    setSelectedGroup(group);
    // For now, open member manager - could create EditGroupPanel later
    setShowGroupMemberManager(true);
  };

  const handleManageGroupMembers = (group: DistributionGroup) => {
    setSelectedGroup(group);
    setShowGroupMemberManager(true);
  };

  const handleCloseGroupMemberManager = () => {
    setShowGroupMemberManager(false);
    setSelectedGroup(null);
  };

  // Render the active section content
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <AdminDashboardTab />;
      case 'mailboxes':
        return (
          <MailboxListView
            onCreateMailbox={handleCreateMailbox}
            onEditMailbox={handleEditMailbox}
            onViewMailbox={handleViewMailbox}
          />
        );
      case 'groups':
        return (
          <GroupListView
            onCreateGroup={handleCreateGroup}
            onEditGroup={handleEditGroup}
            onManageMembers={handleManageGroupMembers}
          />
        );
      case 'domains':
        return <DomainListView />;
      case 'blocked-senders':
        return <BlockedSendersView />;
      case 'rejected-mail':
        return <RejectedMailView />;
      case 'evidence-indexing':
        return <EvidenceIndexingView />;
      case 'migration':
        return <MigrationView />;
      default:
        const item = findNavItem(activeSection);
        if (item) {
          return <PlaceholderContent item={item} />;
        }
        return null;
    }
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-100 dark:bg-gray-900">
      {/* Sidebar Navigation */}
      <AdminSidebar activeSection={activeSection} onSectionChange={setActiveSection} />

      {/* Main Content Area */}
      <div className="flex-1 bg-white dark:bg-gray-900 overflow-auto">
        <div className="p-6">{renderContent()}</div>
      </div>

      {/* Create Mailbox Wizard */}
      <CreateMailboxWizard
        isOpen={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        onSuccess={() => {
          setShowCreateWizard(false);
        }}
      />

      {/* Edit Mailbox Panel */}
      <EditMailboxPanel
        mailbox={selectedMailbox}
        isOpen={showEditPanel}
        onClose={handleCloseEditPanel}
      />

      {/* Create Group Modal */}
      <CreateGroupModal
        isOpen={showCreateGroupModal}
        onClose={() => setShowCreateGroupModal(false)}
        onSuccess={() => {
          setShowCreateGroupModal(false);
        }}
      />

      {/* Group Member Manager Panel */}
      <GroupMemberManager
        group={selectedGroup}
        isOpen={showGroupMemberManager}
        onClose={handleCloseGroupMemberManager}
      />
    </div>
  );
};

export default EdgeMailManagementPage;
